import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Film, Link2, Sparkles, Trash2 } from "lucide-react";
import { api, errMsg, API } from "@/lib/api";

const abs = (u: string) => (u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

function StylePicker({ style, setStyle, aiLeft }: any) {
  const opts = [
    { k: "slideshow", icon: Film, t: "Slideshow", d: "Your real workshop photos and clips, with step captions and soft music. Ready in about a minute." },
    { k: "cinematic", icon: Sparkles, t: "Cinematic AI", d: `AI adds gentle camera motion to up to 4 photos. Takes 5–15 minutes and may change small details. ${aiLeft} left for this piece.` },
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {opts.map(({ k, icon: I, t, d }) => (
        <button key={k} type="button" disabled={k === "cinematic" && !aiLeft} onClick={() => setStyle(k)}
          className={`text-left p-5 border transition-colors disabled:opacity-40 ${style === k ? "border-[#C9A96E] bg-white/5" : "border-white/15 hover:border-white/40"}`} data-testid={`reel-style-${k}`}>
          <I size={18} className="text-[#C9A96E]" /><p className="font-display text-xl mt-3">{t}</p><p className="text-xs text-white/60 mt-1.5 leading-relaxed">{d}</p>
        </button>))}
    </div>
  );
}

function ReelCard({ r, base, k, onChange, onDelete }: any) {
  const shareUrl = `${window.location.origin}/reel/${r.share_token}`;
  const share = async (on: boolean) => {
    try { onChange((await api.put(`${base}/${r.id}/share`, { on }, { params: { k } })).data); if (on) { navigator.clipboard?.writeText(shareUrl); toast("Share link on and copied"); } } catch (e) { toast.error(errMsg(e)); }
  };
  if (r.status !== "ready") return (
    <div className="border border-white/15 p-5 text-sm" data-testid={`reel-${r.id}`}>
      <p className="eyebrow text-[10px] text-[#C9A96E]">{r.style === "cinematic" ? "Cinematic AI" : "Slideshow"}</p>
      {r.status === "rendering" ? <p className="mt-2 flex items-center gap-2 text-white/80" data-testid="reel-rendering"><span className="w-2 h-2 rounded-full bg-[#C9A96E] animate-pulse" />{r.progress || "Working"}…</p>
        : <p className="mt-2 text-red-300" data-testid="reel-failed">Couldn't make this reel: {r.error}</p>}
    </div>
  );
  return (
    <div className="border border-white/15 p-4 grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] gap-5" data-testid={`reel-${r.id}`}>
      <video src={`${abs(r.video_url)}#t=4`} controls playsInline preload="metadata" className="w-full aspect-[9/16] bg-black object-cover" data-testid="reel-video" />
      <div className="min-w-0 space-y-3">
        <p className="eyebrow text-[10px] text-[#C9A96E]">{r.style === "cinematic" ? "Cinematic AI" : "Slideshow"} · {r.duration}s</p>
        {r.note && <p className="text-xs text-white/50">{r.note}</p>}
        <a href={`${abs(r.video_url)}?download=1`} className="inline-flex items-center gap-2 bg-[#C9A96E] text-neutral-900 px-4 py-2 eyebrow text-[10px]" data-testid="reel-download"><Download size={13} />Download</a>
        <label className="flex items-center gap-2 text-xs text-white/80"><input type="checkbox" checked={!!r.shared} onChange={(e) => share(e.target.checked)} data-testid="reel-share-toggle" />Share link — anyone with it can watch (no names or serial shown)</label>
        {r.shared && <div className="flex items-center gap-2 text-xs"><Link2 size={13} className="text-[#C9A96E] shrink-0" /><span className="truncate text-white/60" data-testid="reel-share-url">{shareUrl}</span>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(shareUrl); toast("Link copied"); }} className="underline shrink-0" data-testid="reel-copy-link">Copy</button></div>}
        <button type="button" onClick={() => onDelete(r)} className="text-xs text-white/40 hover:text-white inline-flex items-center gap-1" data-testid="reel-delete"><Trash2 size={12} />Remove</button>
      </div>
    </div>
  );
}

export function ReelStudio({ token, k }: { token: string; k: string }) {
  const base = `/provenance/${token}/reels`;
  const [d, setD] = useState<any>(null);
  const [style, setStyle] = useState("slideshow");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(base, { params: { k } }).then((r) => setD(r.data)).catch(() => {}), [base, k]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d && !d.ai_left) setStyle("slideshow"); }, [d]);
  const rendering = d?.reels?.some((r: any) => r.status === "rendering");
  useEffect(() => { if (!rendering) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [rendering, load]);
  if (!d || !d.has_media) return null;
  const make = async () => {
    setBusy(true);
    try { const r = await api.post(base, { style }, { params: { k } }); setD({ ...d, reels: [r.data, ...d.reels], ai_left: d.ai_left - (style === "cinematic" ? 1 : 0) }); toast("Making your reel…"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upd = (r: any) => setD({ ...d, reels: d.reels.map((x: any) => (x.id === r.id ? r : x)) });
  const del = async (r: any) => {
    if (!window.confirm("Remove this reel? Its share link will stop working.")) return;
    try { await api.delete(`${base}/${r.id}`, { params: { k } }); setD({ ...d, reels: d.reels.filter((x: any) => x.id !== r.id) }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mt-20 border-t border-white/15 pt-14" data-testid="reel-studio">
      <p className="eyebrow text-[10px] text-[#C9A96E] text-center">For The Big Day</p>
      <h2 className="font-display font-light text-3xl sm:text-4xl text-center mt-3">Your making-of reel</h2>
      <p className="text-sm text-white/60 text-center mt-3 max-w-lg mx-auto">Turn every completed step into a short vertical video to share when you're ready. Choose a style, then download it or share a private link.</p>
      <div className="mt-8"><StylePicker style={style} setStyle={setStyle} aiLeft={d.ai_left} /></div>
      <div className="text-center mt-6"><button disabled={busy || rendering} onClick={make} className="border border-[#C9A96E] text-[#C9A96E] px-8 py-3 eyebrow text-[11px] hover:bg-[#C9A96E] hover:text-neutral-900 transition-colors disabled:opacity-40" data-testid="reel-create">{rendering ? "Your reel is being made…" : "Make my reel"}</button></div>
      {d.reels.length > 0 && <div className="mt-10 space-y-4" data-testid="reel-list">{d.reels.map((r: any) => <ReelCard key={r.id} r={r} base={base} k={k} onChange={upd} onDelete={del} />)}</div>}
    </section>
  );
}
